// This module owns organization-scoped Site Health query keys, React hooks, status polling, and cache cleanup. API requests belong to siteHealthService.

import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/provider/useAuth'
import { useFetcher } from '@/provider/useFetcher'
import { ApiError } from './api'
import { isActiveSiteHealthAuditStatus } from './siteHealth'
import type {
  SiteHealthAuditRequest,
  SiteHealthAuditStatusResponse,
  SiteHealthAuditsResponse,
} from './siteHealth.types'
import {
  fetchSiteHealthAuditStatus,
  fetchSiteHealthAudits,
  fetchSiteHealthFindingTypes,
  fetchSiteHealthOverview,
  fetchSiteHealthPageInspector,
  fetchSiteHealthPageSnapshot,
  fetchSiteHealthPages,
  submitSiteHealthAudit,
} from './siteHealthService'

const POLL_MS = 2000

interface SiteHealthStatusPollQuery {
  state: {
    data?: SiteHealthAuditStatusResponse | null
  }
}

interface SiteHealthAuditsPollQuery {
  state: {
    data?: SiteHealthAuditsResponse | null
  }
}

export const siteHealthKeys = {
  org: (orgId: string) => ['siteHealth', orgId] as const,
  audits: (orgId: string) => ['siteHealth', orgId, 'audits'] as const,
  status: (orgId: string, auditId: string) =>
    ['siteHealth', orgId, 'audits', auditId, 'status'] as const,
  overview: (orgId: string, auditId: string) =>
    ['siteHealth', orgId, 'audits', auditId, 'overview'] as const,
  pages: (orgId: string, auditId: string, cursor: string | null) =>
    ['siteHealth', orgId, 'audits', auditId, 'pages', cursor] as const,
  findingTypes: (orgId: string, auditId: string, cursor: string | null) =>
    ['siteHealth', orgId, 'audits', auditId, 'finding-types', cursor] as const,
  inspector: (orgId: string, auditId: string, pageId: string) =>
    [
      'siteHealth',
      orgId,
      'audits',
      auditId,
      'pages',
      pageId,
      'inspector',
    ] as const,
  snapshot: (orgId: string, auditId: string, pageId: string) =>
    [
      'siteHealth',
      orgId,
      'audits',
      auditId,
      'pages',
      pageId,
      'snapshot',
    ] as const,
}

export function siteHealthAuditsRefetchInterval(
  query: SiteHealthAuditsPollQuery
): number | false {
  const hasActiveAudit = query.state.data?.audits.some((audit) =>
    isActiveSiteHealthAuditStatus(audit.status)
  )

  return hasActiveAudit ? POLL_MS : false
}

export function siteHealthRefetchInterval(
  query: SiteHealthStatusPollQuery
): number | false {
  const status = query.state.data?.status

  if (status === 'complete' || status === 'partial' || status === 'error') {
    return false
  }

  return POLL_MS
}

export function useSubmitSiteHealthAudit() {
  const fetcher = useFetcher()
  const auth = useAuth()
  const queryClient = useQueryClient()
  const orgId = auth?.orgId

  function refetchAudits() {
    if (!orgId) return Promise.resolve()

    return queryClient.invalidateQueries({
      queryKey: siteHealthKeys.audits(orgId),
      exact: true,
    })
  }

  return useMutation({
    mutationFn: (request: SiteHealthAuditRequest) =>
      submitSiteHealthAudit(fetcher, request),
    onSuccess: refetchAudits,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        return refetchAudits()
      }
    },
  })
}

export function useSiteHealthAudits() {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.audits(orgId)
      : (['siteHealth', 'disabled', 'audits'] as const),
    queryFn: ({ signal }) => fetchSiteHealthAudits(fetcher, signal),
    enabled: Boolean(orgId),
    refetchInterval: siteHealthAuditsRefetchInterval,
    refetchIntervalInBackground: false,
  })
}

export function useSiteHealthAuditStatus(auditId: string) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.status(orgId, auditId)
      : (['siteHealth', 'disabled', 'status'] as const),
    queryFn: ({ signal }) =>
      fetchSiteHealthAuditStatus(fetcher, auditId, signal),
    enabled: Boolean(orgId && auditId),
    refetchInterval: siteHealthRefetchInterval,
    refetchIntervalInBackground: false,
  })
}

export function useSiteHealthOverview(auditId: string, enabled = true) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.overview(orgId, auditId)
      : (['siteHealth', 'disabled', 'overview'] as const),
    queryFn: ({ signal }) => fetchSiteHealthOverview(fetcher, auditId, signal),
    enabled: enabled && Boolean(orgId && auditId),
  })
}

export function useSiteHealthPages(
  auditId: string,
  cursor: string | null = null,
  enabled = true
) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.pages(orgId, auditId, cursor)
      : (['siteHealth', 'disabled', 'pages', cursor] as const),
    queryFn: ({ signal }) =>
      fetchSiteHealthPages(fetcher, auditId, cursor, signal),
    enabled: enabled && Boolean(orgId && auditId),
  })
}

export function useSiteHealthFindingTypes(
  auditId: string,
  cursor: string | null = null,
  enabled = true
) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.findingTypes(orgId, auditId, cursor)
      : (['siteHealth', 'disabled', 'finding-types', cursor] as const),
    queryFn: ({ signal }) =>
      fetchSiteHealthFindingTypes(fetcher, auditId, cursor, signal),
    enabled: enabled && Boolean(orgId && auditId),
  })
}

export function useSiteHealthPageInspector(auditId: string, pageId: string) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.inspector(orgId, auditId, pageId)
      : (['siteHealth', 'disabled', 'inspector'] as const),
    queryFn: ({ signal }) =>
      fetchSiteHealthPageInspector(fetcher, auditId, pageId, signal),
    enabled: Boolean(orgId && auditId && pageId),
  })
}

export function useSiteHealthPageSnapshot(
  auditId: string,
  pageId: string,
  enabled: boolean
) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? siteHealthKeys.snapshot(orgId, auditId, pageId)
      : (['siteHealth', 'disabled', 'snapshot'] as const),
    queryFn: ({ signal }) =>
      fetchSiteHealthPageSnapshot(fetcher, auditId, pageId, signal),
    enabled: enabled && Boolean(orgId && auditId && pageId),
  })
}

export async function clearSiteHealthOrgCache(
  queryClient: QueryClient,
  orgId: string
): Promise<void> {
  const key = siteHealthKeys.org(orgId)

  await queryClient.cancelQueries({ queryKey: key })
  queryClient.removeQueries({ queryKey: key })
}
